import httpClient, { ConexaResponse } from '@/services/httpClient'
import platform from '@/utils/platform'
import {
  ImmediateAttendanceData,
  ImmediateAttendanceResponse,
  IRequestCallTokenGroupResponse,
  IRequestCallTokenGroupReturnType,
  IRequestCallTokenResponse,
  IRequestTokenData,
} from './types'

interface SendMetricsPayload {
  callingTimes: number
  status: string
}

export const sendMetrics = async (
  callId: number,
  payload: SendMetricsPayload,
): Promise<void> => {
  const data = {
    quantidadeAlertasChamada: payload.callingTimes,
    abaPlataformaEmSegundoPlano: document.visibilityState === 'hidden',
    status: payload.status,
    plataforma: 'WEB',
    dispositivo: platform.device(),
    sistemaOperacional: platform.os(),
    navegador: platform.browser(),
  }
  await httpClient
    .post(
      `/integration/app-paciente/atendimento/chamada/${callId}/metrica`,
      data,
    )
    .catch(err => console.log(err))
}

export const validateCallToken = async (
  idAtendimento: string,
  idProfissional: string,
  date: string,
  tokenAutorizacaoExterno: string,
  numeroGuiaPrestador: string,
): Promise<void> => {
  const dataHoraAgendamento = date.replace(',', '')
  return httpClient
    .post(
      'integration/app-empresa-externa/elegibilidade/chamada/validar-token',
      {
        idAtendimento,
        idProfissional,
        dataHoraAgendamento,
        tokenAutorizacaoExterno,
        numeroGuiaPrestador,
      },
    )
    .then(d => d.data.object)
}

export const requestCallToken = async (
  requestData: IRequestTokenData,
): Promise<ConexaResponse<IRequestCallTokenResponse>> => {
  const { idAtendimento, idProfissional, dataHoraAgendamento } = requestData
  const response = await httpClient.post(
    'integration/app-empresa-externa/elegibilidade/chamada/solicitar-token',
    {
      idAtendimento,
      idProfissional,
      dataHoraAgendamento,
    },
  )
  return response.data
}

export const verifyTokenStatus = async (
  attendanceId: number,
): Promise<boolean> => {
  try {
    const response = await httpClient.get(
      `/integration/app-empresa-externa/elegibilidade/atendimento/${attendanceId}/token`,
    )
    return response.data.object
  } catch (err) {
    console.error(err)
  }
}

export const validateCallTokenGroup = async (
  idAtendimento: string,
  idProfissional: string,
  dataHoraAgendamento: string,
  tokenAutorizacaoExterno: string,
  numeroGuiaPrestador: string,
): Promise<void> => {
  return httpClient
    .post('/eligibility/validate-token', {
      appointmentGroupId: idAtendimento,
      professionalId: idProfissional,
      date: dataHoraAgendamento,
      authorizationToken: tokenAutorizacaoExterno,
      providerGuideNumber: numeroGuiaPrestador,
    })
    .then(d => d.data.object)
}

export const requestCallTokenGroup = async (
  idAtendimento: string,
  idProfissional: string,
  dataHoraAgendamento: string,
): Promise<IRequestCallTokenGroupReturnType> => {
  const response = await httpClient.post<
    ConexaResponse<IRequestCallTokenGroupResponse>
  >('/eligibility/request-token', {
    appointmentGroupId: idAtendimento,
    professionalId: idProfissional,
    date: dataHoraAgendamento,
  })

  const parsedResponse: IRequestCallTokenGroupReturnType = {
    elegible: response.data.object.elegivel,
    providerGuideNumber: response.data.object.numeroGuiaPrestador,
    operatorGuideNumber: response.data.object.numeroGuiaOperadora,
    message: response.data.object.mensagem,
    validatedToken: response.data.object.tokenJaValidado,
  }

  return parsedResponse
}
// firebase "v2" com dados de endpoint
export const getImmediateDataFromRealTime =
  async (): Promise<ImmediateAttendanceData> => {
    const response = await httpClient.get<
      ConexaResponse<ImmediateAttendanceResponse>
    >(`/dados-atendimento/imediato`)
    const data = response?.data?.object
    return {
      id: data?.id,
      protocolId: data?.idProtocolo,
      preQueueId: data?.idPreFila,
      attendanceId: data?.idAtendimento,
      patientId: data?.idPaciente,
      professionalId: data?.idProfissional,
      clinicId: data?.idClinica,
      queueSize: data?.tamanhoFila,
      professionalTypeId: data?.idTipoProfissional,
      professionalType: data?.tipoProfissional,
      professionalName: data?.nomeProfissional,
      professionalSex: data?.sexoProfissional,
      professionalPhoto: data?.fotoProfissional,
      estimatedTime: data?.tempoEstimado,
      initialEstimatedTime: data?.tempoEstimadoInicial,
      patientNotificatedAttendanceSoon:
        data?.pacienteNotificadoAtendimentoEmBreve,
      patientOnline: data?.pacienteOnline,
      professionalSpeciality: data?.especialidadeProfissional,
      npsAnswered: data?.npsRespondido,
      scheduleStatus: data?.statusAgendamento,
      dateAndTimeScheduled: data?.dataHoraAgendada,
      attachments: data?.anexos?.map(attachment => {
        return {
          attachmentId: attachment?.idAnexo,
          attachmentAttendanceId: attachment?.idAtendimentoAnexo,
          base64: attachment?.conteudoBase64,
          extensionName: attachment?.nomeExtensao,
          label: attachment?.legenda,
          urlDownload: attachment?.urlDownload,
          size: attachment?.tamanho,
          examDate: attachment?.dataExame,
          attachmentType: attachment?.tipoAnexo,
          professionalAttachment: attachment?.anexoProfissional,
        }
      }),
      minWaitingTime: data?.tempoMinimoEspera,
      maxWaitingTime: data?.tempoMaximoEspera,
      psyAliveAttendance: data?.atendimentoPsicologiaViva,
      attendanceLink: data?.linkAtendimento,
      minHour: data?.horaMinima,
      maxHour: data?.horaMaxima,
      paymentMethod: data?.metodoPagamento,
      attendanceConfirmed: data?.consultaConfirmada,
      attendanceToken: data?.tokenAtendimento,
      professionalSpecialityId: data?.idEspecialidadeProfissional,
      flagScreeningProfessional: data?.flagProfissionalTriagista,
      unifiedAttendance: data?.atendimentoUnificado,
      authMessage: data?.mensagemAutorizacao,
      attendanceMode: data?.modoAtendimento,
      call: {
        callId: data?.chamada?.idChamada,
        attendanceId: data?.chamada?.idAtendimento,
        attendanceMode: data?.chamada?.modoAtendimento,
        attendanceToken: data?.chamada?.tokenAtendimento,
        callToken: data?.chamada?.tokenChamada,
        meetingType: data?.chamada?.tipoMeeting,
        name: data?.chamada?.nome,
        professionalId: data?.chamada?.idProfissional,
        professionalRegister: data?.chamada?.registroProfissional,
        scheduleStatus: data?.chamada?.statusAgendamento,
        professionalName: data?.chamada?.nomeProfissional,
        specialityId: data?.chamada?.idEspecialidade,
        specialityName: data?.chamada?.nomeEspecialidade,
        unifiedAttendance: data?.chamada?.atendimentoUnificado,
        zoomMeetingId: data?.chamada?.zoomMeetingId,
        zoomMeetingPasswd: data?.chamada?.zoomMeetingPasswd,
        zoomMeetingUrl: data?.chamada?.zoomMeetingUrl,
        zoomMeetingUrlPatient: data?.chamada?.zoomMeetingUrlPaciente,
        queueStatus: data?.chamada?.statusFila,
      },
    }
  }
